import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { X, Search, ArrowUp, ArrowDown } from 'lucide-react';
import { useLanguage } from '@/hooks/useLanguage';

interface OrderFiltersProps {
  statusFilter: string;
  searchQuery: string;
  dateFilter: string;
  sortBy: string;
  sortOrder: 'asc' | 'desc';
  onStatusChange: (value: string) => void;
  onSearchChange: (value: string) => void;
  onDateChange: (value: string) => void;
  onSortByChange: (value: string) => void;
  onSortOrderToggle: () => void;
  onClearFilters: () => void;
}

export const OrderFilters = ({
  statusFilter,
  searchQuery,
  dateFilter,
  sortBy,
  sortOrder,
  onStatusChange,
  onSearchChange,
  onDateChange,
  onSortByChange,
  onSortOrderToggle,
  onClearFilters
}: OrderFiltersProps) => {
  const { t } = useLanguage();
  
  return (
    <div className="space-y-4 p-4 bg-muted/50 rounded-lg border mb-4">
      <div className="flex items-center justify-between">
        <h3 className="font-semibold flex items-center gap-2">
          <Search className="h-4 w-4" />
          {t('orders.filters.title', 'Filtres, recherche et tri')}
        </h3>
        <Button variant="ghost" size="sm" onClick={onClearFilters}>
          <X className="h-4 w-4 mr-2" />
          {t('orders.filters.reset', 'Réinitialiser')}
        </Button>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="space-y-2">
          <Label>{t('orders.filters.status', 'Statut')}</Label>
          <Select value={statusFilter} onValueChange={onStatusChange}>
            <SelectTrigger>
              <SelectValue placeholder={t('orders.filters.all_statuses', 'Tous les statuts')} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t('orders.filters.all', 'Tous les statuts')}</SelectItem>
              <SelectItem value="PENDING_PAYMENT">{t('orders.status.PENDING_PAYMENT', 'En attente paiement')}</SelectItem>
              <SelectItem value="PAID">{t('orders.status.PAID', 'Payé')}</SelectItem>
              <SelectItem value="PREPARING">{t('orders.status.PREPARING', 'En préparation')}</SelectItem>
              <SelectItem value="READY_FOR_PICKUP">{t('orders.status.READY_FOR_PICKUP', 'Prêt pour retrait')}</SelectItem>
              <SelectItem value="PICKED_UP">{t('orders.status.PICKED_UP', 'Récupéré')}</SelectItem>
              <SelectItem value="CANCELLED">{t('orders.status.CANCELLED', 'Annulé')}</SelectItem>
              <SelectItem value="EXPIRED">{t('orders.status.EXPIRED', 'Expiré')}</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <Label>{t('orders.filters.search', 'Recherche')}</Label>
          <Input
            placeholder={t('orders.filters.vendor_search_placeholder', 'N° commande, produit, point relais...')}
            value={searchQuery}
            onChange={(e) => onSearchChange(e.target.value)}
          />
        </div>

        <div className="space-y-2">
          <Label>{t('orders.filters.period', 'Période')}</Label>
          <Select value={dateFilter} onValueChange={onDateChange}>
            <SelectTrigger>
              <SelectValue placeholder={t('orders.filters.all_periods', 'Toutes les périodes')} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t('orders.filters.all_periods', 'Toutes les périodes')}</SelectItem>
              <SelectItem value="today">{t('orders.filters.today', 'Aujourd\'hui')}</SelectItem>
              <SelectItem value="week">{t('orders.filters.week', 'Cette semaine')}</SelectItem>
              <SelectItem value="month">{t('orders.filters.month', 'Ce mois')}</SelectItem>
              <SelectItem value="year">{t('orders.filters.year', 'Cette année')}</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <Label>{t('orders.filters.sort_by', 'Trier par')}</Label>
          <div className="flex gap-2">
            <Select value={sortBy} onValueChange={onSortByChange}>
              <SelectTrigger className="flex-1">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="created_at">{t('orders.filters.sort_date', 'Date de création')}</SelectItem>
                <SelectItem value="amount">{t('orders.filters.sort_amount', 'Montant')}</SelectItem>
                <SelectItem value="status">{t('orders.filters.sort_status', 'Statut')}</SelectItem>
                <SelectItem value="order_number">{t('orders.filters.sort_number', 'N° commande')}</SelectItem>
              </SelectContent>
            </Select>
            <Button
              variant="outline"
              size="icon"
              onClick={onSortOrderToggle}
            >
              {sortOrder === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};
